/**
 * Outputs duration of given fractional hours in the format of "Xh YYm"
 * Examples: "8h 30m", "8h 00m", "0h 45m"
 * When `areLeadingZeroesAllowed` is `false`, it returns "45m", "7m", "1m"
 * When `omitZeroMinutes` is `true`, it returns "8h" instead of "8h 00m"
 * Returns "0h 00m" or "0m" for zero or negative durations
 */
export function formatHoursDurationCompact(
  durationInHours: number,
  options?: { areLeadingZeroesAllowed?: boolean; omitZeroMinutes?: boolean }
): string {
  const { areLeadingZeroesAllowed = true, omitZeroMinutes = false } = options ?? {};
  const durationInMinutes = Math.round(durationInHours * 60);

  if (durationInMinutes <= 0) {
    if (omitZeroMinutes) {
      return "0h";
    }
    return areLeadingZeroesAllowed ? "0h 00m" : "0m";
  }

  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;

  // If minutes are zero and omitZeroMinutes is true, return just hours
  if (minutes === 0 && omitZeroMinutes) {
    return `${hours}h`;
  }

  const hasHours = areLeadingZeroesAllowed || hours > 0;
  const hoursString = hasHours ? `${hours}h ` : "";

  // Ensure minutes are always two digits when appropriate
  const paddedMinutes = hasHours ? minutes.toString().padStart(2, "0") : minutes;

  return `${hoursString}${paddedMinutes}m`;
}
